import { CruiseDay, ExploreByDayCarouselProps } from '@msc-b2c/interfaces/types';
import { Carousel } from '@msc-b2c/shared-components';
import DayCarouselCard from './DayCarouselCard';
import {
  addDaysAtSea,
  AppDispatch,
  breakpoints,
  daySelected,
  getActBaseImagePath,
  getActPlaceholderImagePath,
  getDynamicMediaPath,
  getLabCDConfig,
} from '@msc-b2c/shared-utils';
import {
  findFirstExcursionForDay,
  resolveDayCardBackgroundImage,
  resolveCardBackgroundImage,
} from './daycarouselutils';
import styles from './explorebydaycarousel.module.scss';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

const ExploreByDayCarousel = ({
  configurationProps,
  excursionDataProps,
  itineraryDataProps,
  portImagesDataProps,
}: ExploreByDayCarouselProps) => {
  const [isMobile, setIsMobile] = useState<boolean>(globalThis.innerWidth <= breakpoints.md);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const urlCountry = getActBaseImagePath() || '/content/dam/msc-cruises/b2c-assets/act';
  const placeholderImagePath = getActPlaceholderImagePath();

  const defaultSelectedDay = {
    portCode: '',
    date: 'allPorts',
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(globalThis.innerWidth <= breakpoints.md);

    globalThis.addEventListener('resize', handleResize);
    return () => globalThis.removeEventListener('resize', handleResize);
  }, []);

  // Add days at sea
  const updatedDataWithDaysAtSea =
    itineraryDataProps && addDaysAtSea(itineraryDataProps, configurationProps.dayAtSeaLabel);

  // Filter out days that have no excursions
  const filteredDays =
    updatedDataWithDaysAtSea?.days
      .map((day, index) => ({
        ...day,
        originalIndex: index + 1, // Store original index (1-based)
      }))
      .filter((day) => findFirstExcursionForDay(day, excursionDataProps || [])) || [];

  const usedImagesByPortCode = new Map<string, Set<string>>();
  const cardBackgroundImages = filteredDays.map((dayItem) => {
    const isDayAtSea =
      'dayAtSea' in dayItem && Boolean((dayItem as { dayAtSea?: boolean }).dayAtSea);
    const portCode = isDayAtSea ? 'SEADAY' : dayItem.port.code;

    return resolveDayCardBackgroundImage({
      portCode: portCode,
      portsData: portImagesDataProps,
      usedImagesByPortCode,
    });
  });

  // Function to determine departureInfo for each day
  const getDepartureInfo = (dayData: CruiseDay): string => {
    let newDepartureInfo = '';

    // Times are already formatted (context-aware) by `addDaysAtSea`.
    const arrivalTimeFormatted = dayData.arrivalTime || '';
    const departureTimeFormatted = dayData.departureTime || '';

    if (dayData.port.code === 'SEA' && configurationProps.dayAtSeaInfoLabel) {
      newDepartureInfo = configurationProps.dayAtSeaInfoLabel;
    } else if (
      arrivalTimeFormatted &&
      !departureTimeFormatted &&
      configurationProps.arrivalOnlyInfoLabel
    ) {
      newDepartureInfo = configurationProps.arrivalOnlyInfoLabel.replace(
        '{arrivalTime}',
        arrivalTimeFormatted
      );
    } else if (
      !arrivalTimeFormatted &&
      departureTimeFormatted &&
      configurationProps.departureOnlyInfoLabel
    ) {
      newDepartureInfo = configurationProps.departureOnlyInfoLabel.replace(
        '{departureTime}',
        departureTimeFormatted
      );
    } else if (
      arrivalTimeFormatted &&
      departureTimeFormatted &&
      configurationProps.touchingPortInfoLabel
    ) {
      newDepartureInfo = configurationProps.touchingPortInfoLabel
        .replace('{arrivalTime}', arrivalTimeFormatted)
        .replace('{departureTime}', departureTimeFormatted);
    }

    // --- Handle Ship Position Placeholder ---
    if (newDepartureInfo.includes('{shipPosition}')) {
      const itineraryDataLabCDCode = dayData.shipPosition?.code;
      const itineraryDataLabCDDesc = dayData.shipPosition?.description;
      const contextAwareLabCDConfig = getLabCDConfig();

      // Find a matching ship position based on configuration
      const matchingCode = contextAwareLabCDConfig.find(
        (context) =>
          itineraryDataLabCDCode && context.code === Number.parseInt(itineraryDataLabCDCode)
      );

      let shipPositionLabel = '';

      if (matchingCode) {
        shipPositionLabel = matchingCode.position;
      } else if (itineraryDataLabCDDesc) {
        shipPositionLabel = itineraryDataLabCDDesc;
      }

      if (!shipPositionLabel) {
        newDepartureInfo = newDepartureInfo.replace('{shipPosition}', '');
      } else {
        newDepartureInfo = newDepartureInfo.replace('{shipPosition}', shipPositionLabel);
      }
    }

    return newDepartureInfo;
  };

  const ctaButtonNavigationAction = () => {
    dispatch(daySelected(defaultSelectedDay));
    navigate(`/excursions${globalThis.location.href.includes('.html') ? '.html' : ''}`); // Redirect to excursions page TODO: Make the value dynamic"
  };

  return (
    <div className={styles.dayCarouselContainer}>
      <Carousel
        id={configurationProps.dataComponentName}
        data={filteredDays.length > 0 ? filteredDays : []}
        swiperOptions={{
          slidesPerView: 'auto',
          spaceBetween: 16,
          loop: false,
          speed: 200,
          breakpointsBase: 'container',
        }}
        isProgressBar={true}
        isFreeMode={true}
        showIndicator={{
          mobile: false,
          tablet: false,
          desktop: false,
        }}
        showInNavIndicator={{
          mobile: configurationProps.showSliderMobile,
          tablet: configurationProps.showSliderDesktop,
          desktop: configurationProps.showSliderDesktop,
        }}
        showArrows={
          !!(configurationProps.scrollLeftIcon && configurationProps.scrollRightIcon) &&
          ((isMobile && configurationProps.showSliderMobile) ||
            (!isMobile && configurationProps.showSliderDesktop))
        }
        buttonLinkLabel={configurationProps.ctaLinkLabel}
        buttonLinkIcon={configurationProps.ctaLinkIcon}
        buttonClick={() => {
          ctaButtonNavigationAction();
        }}
        styleOption={'-circular-actions -medium-actions -light'}
        renderItem={(dayData: object, index: number) => {
          const dayItem = dayData as CruiseDay;
          const firstExcursionOfDay = findFirstExcursionForDay(dayItem, excursionDataProps || []);
          const excursionCode = firstExcursionOfDay?.code ?? '';
          // Excursion image: prefer Dynamic Media, fallback to AEM DAM path
          const excursionImageDm = getDynamicMediaPath(excursionCode);
          const excursionImageFallback = excursionCode ? `${urlCountry}/${excursionCode}.jpg` : '';
          const excursionImage = excursionImageDm || excursionImageFallback;
          // Resolve background image: prioritize based on configuration
          // Both sources are Dynamic Media–compatible; fallback ensures coverage
          const backgroundImage = resolveCardBackgroundImage(
            configurationProps.portImagesEnabled,
            cardBackgroundImages[index],
            excursionImage
          );
          const departureInfo = getDepartureInfo(dayItem);
          return (
            <DayCarouselCard
              key={`day-carousel-card-${index}`}
              dayIndex={dayItem.originalIndex ?? index}
              dayData={dayItem}
              dayPlaceHolder={configurationProps.dayPlaceHolder}
              dayCardDateFormat={configurationProps.dayCardDateFormat}
              departureInfo={departureInfo}
              backgroundImage={backgroundImage}
              placeholderImagePath={placeholderImagePath}
              navigationLink={
                configurationProps.rediractionLinkUrl.valid &&
                configurationProps.rediractionLinkUrl.url
                  ? configurationProps.rediractionLinkUrl.url
                  : ''
              }
              showOceanKayPill={dayItem.port.code === '1SC'}
              oceanKayPillLabel={configurationProps.oceanKayRibbonLabel}
              oceanKayPillBgColor={configurationProps.oceanKayRibbonBgColor}
              oceanKayPillTextColor={configurationProps.oceanKayRibbonTextColor}
            />
          );
        }}
      />
    </div>
  );
};

export default ExploreByDayCarousel;
